// src/components/clients/StatusPipeline.tsx
//
// 4-node status pipeline strip. Replaces the "Move to Applied" button as the
// header status-advance affordance. Shows where the client is in the funnel
// + supports forward advance (click any node) and backward regress (with
// confirm). Stages: New Client → Touring → Applied → Payment Received.
//
// Out-of-pipeline statuses (out_of_contact · closed) render as a chip above
// the strip with a "Return to pipeline" hint.

import React, { useState } from "react";
import {
  Box,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { statusConfig, pipelineStatuses } from "./config";
import type { ClientStatus } from "./types";

interface StatusPipelineProps {
  currentStatus: ClientStatus;
  onAdvance: (newStatus: ClientStatus) => void;
}

const StatusPipeline: React.FC<StatusPipelineProps> = ({
  currentStatus,
  onAdvance,
}) => {
  const [confirmRegress, setConfirmRegress] = useState<ClientStatus | null>(null);

  const currentIndex = pipelineStatuses.indexOf(currentStatus);
  const isOffPipeline = currentIndex === -1;

  const handleNodeClick = (target: ClientStatus, targetIndex: number) => {
    if (target === currentStatus) return;
    if (targetIndex < currentIndex) {
      setConfirmRegress(target);
    } else {
      onAdvance(target);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {isOffPipeline && (
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: statusConfig[currentStatus].color,
              fontWeight: 600,
              fontSize: "0.72rem",
              letterSpacing: 0.3,
              textTransform: "uppercase",
            }}
          >
            {statusConfig[currentStatus].label}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          position: "relative",
        }}
      >
        {pipelineStatuses.map((status, idx) => {
          const isCurrent = status === currentStatus;
          const isPast = idx < currentIndex;
          const isFuture = idx > currentIndex;
          const cfg = statusConfig[status];
          const dotColor = isCurrent || isPast ? cfg.color : "rgba(255,255,255,0.25)";
          const dotFill = isCurrent || isPast ? cfg.color : "transparent";

          return (
            <React.Fragment key={status}>
              {idx > 0 && (
                <Box
                  sx={{
                    flex: 1,
                    height: 2,
                    backgroundColor:
                      isPast || isCurrent
                        ? statusConfig[pipelineStatuses[idx - 1]].color
                        : "rgba(255,255,255,0.15)",
                    transition: "background-color 200ms ease",
                  }}
                />
              )}
              <Tooltip
                title={
                  isCurrent
                    ? `Current: ${cfg.label}`
                    : isFuture
                    ? `Advance to ${cfg.label}`
                    : `Go back to ${cfg.label}`
                }
                arrow
                placement="top"
              >
                <Box
                  onClick={() => handleNodeClick(status, idx)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleNodeClick(status, idx);
                    }
                  }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: isCurrent ? "default" : "pointer",
                    flexShrink: 0,
                    px: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      width: isCurrent ? 18 : 14,
                      height: isCurrent ? 18 : 14,
                      borderRadius: "50%",
                      border: `2px solid ${dotColor}`,
                      backgroundColor: dotFill,
                      transition: "all 180ms ease",
                      boxShadow: isCurrent
                        ? `0 0 0 4px ${dotColor}33`
                        : "none",
                      "&:hover": {
                        backgroundColor: !isCurrent ? `${cfg.color}55` : dotFill,
                        borderColor: cfg.color,
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      mt: 0.75,
                      fontSize: "0.7rem",
                      fontWeight: isCurrent ? 700 : 500,
                      color: isCurrent ? "white" : "rgba(255,255,255,0.65)",
                      whiteSpace: "nowrap",
                      letterSpacing: 0.2,
                    }}
                  >
                    {cfg.label}
                  </Typography>
                </Box>
              </Tooltip>
            </React.Fragment>
          );
        })}
      </Box>

      <Dialog
        open={confirmRegress !== null}
        onClose={() => setConfirmRegress(null)}
      >
        <DialogTitle>Move client back?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This client is currently <b>{statusConfig[currentStatus].label}</b>.
            Move them back to{" "}
            <b>
              {confirmRegress ? statusConfig[confirmRegress].label : ""}
            </b>
            ? The history stays · status changes are tracked in the Timeline.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRegress(null)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              if (confirmRegress) onAdvance(confirmRegress);
              setConfirmRegress(null);
            }}
          >
            Move back
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StatusPipeline;
